import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, ScrollText, Filter, Hammer } from 'lucide-react';
import axios from 'axios';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Ledger() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [agency, setAgency] = useState('');
  const [jurisdictionFilter, setJurisdictionFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (agency) params.agency = agency;
      if (jurisdictionFilter) params.jurisdiction_contains = jurisdictionFilter;
      const [feed, st] = await Promise.all([
        axios.get(`${API}/ledger/feed`, { params }),
        axios.get(`${API}/ledger/stats`),
      ]);
      setItems(feed.data.items || []);
      setSetupRequired(!!feed.data.setup_required || !!st.data.setup_required);
      setStats(st.data);
    } catch (e) {
      setItems([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const onFilter = (e) => { e.preventDefault(); load(); };

  return (
    <div className="min-h-screen bg-plai-bg flex flex-col" data-testid="ledger-page">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><Logo size="sm" /></Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="font-body text-xs tracking-[0.2em] uppercase text-white/70 hover:text-white inline-flex items-center gap-1" data-testid="ledger-back-home">
              <ArrowLeft className="h-3 w-3" strokeWidth={1.5} /> Home
            </Link>
            <Link to="/auth?mode=signup" className="btn-primary-cta text-xs px-4 py-2" data-testid="ledger-cta-signup">Join The Resistance</Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-12">
        <div className="mb-10">
          <p className="font-body text-xs tracking-[0.3em] uppercase text-plai-cta mb-3">PUBLIC RECORD</p>
          <h1 className="font-headings text-5xl sm:text-6xl uppercase tracking-tight mb-3">Ledger of Resistance</h1>
          <p className="font-body text-sm sm:text-base text-white/70 max-w-3xl leading-relaxed">
            Every notice on this page was filed by a citizen who chose to make it public. We never show names, emails, or personal stories. Only the land. Only the agency. Only the date.
          </p>
        </div>

        {setupRequired && (
          <div className="glass iridescent-border p-6 mb-8" data-testid="ledger-setup-banner">
            <h3 className="font-headings text-lg uppercase mb-2 text-plai-cta">Migration Required</h3>
            <p className="font-body text-sm text-white/75">Run <code className="bg-black px-2 py-1">/app/backend/database_migration_v2.sql</code> in your Supabase SQL Editor to enable the Ledger.</p>
          </div>
        )}

        {/* Stats strip */}
        {stats && !setupRequired && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10" data-testid="ledger-stats">
            <div className="tactical-card p-5">
              <p className="font-body text-[10px] tracking-[0.2em] uppercase text-white/50">Public Notices</p>
              <p className="font-headings text-4xl mt-1 text-plai-cta" data-testid="stat-total">{stats.total_public_notices}</p>
            </div>
            {Object.entries(stats.by_agency || {}).slice(0, 3).map(([k, v]) => (
              <div key={k} className="tactical-card p-5" data-testid={`stat-agency-${k}`}>
                <p className="font-body text-[10px] tracking-[0.2em] uppercase text-white/50">{k}</p>
                <p className="font-headings text-4xl mt-1">{v}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <form onSubmit={onFilter} className="flex flex-col sm:flex-row gap-3 mb-8" data-testid="ledger-filters">
          <div className="flex-1 relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" strokeWidth={1.5} />
            <input
              value={jurisdictionFilter}
              onChange={(e) => setJurisdictionFilter(e.target.value)}
              placeholder="Filter by jurisdiction or place"
              className="w-full pl-10 pr-3 py-3 text-sm"
              data-testid="ledger-filter-jurisdiction"
            />
          </div>
          <select
            value={agency}
            onChange={(e) => setAgency(e.target.value)}
            className="px-3 py-3 text-sm"
            data-testid="ledger-filter-agency"
          >
            <option value="">All agencies</option>
            <option value="BLM">BLM</option>
            <option value="NPS">NPS</option>
            <option value="USFS">USFS</option>
            <option value="USFWS">USFWS</option>
            <option value="DHS">DHS</option>
            <option value="EPA">EPA</option>
            <option value="USACE">USACE</option>
            <option value="STATE">State</option>
            <option value="COUNTY">County</option>
            <option value="CITY">City</option>
          </select>
          <button type="submit" className="btn-ghost" data-testid="ledger-apply-filter-btn">
            <Filter className="h-3 w-3 inline mr-1" strokeWidth={1.5} /> Apply
          </button>
        </form>

        {/* Feed */}
        {loading ? (
          <p className="font-body text-sm text-white/50" data-testid="ledger-loading">Loading...</p>
        ) : items.length === 0 ? (
          <div className="tactical-card p-12 text-center" data-testid="ledger-empty">
            <ScrollText className="h-12 w-12 text-white/20 mx-auto mb-4" strokeWidth={1} />
            <p className="font-body text-sm text-white/60 mb-1">No public notices yet.</p>
            <p className="font-body text-xs text-white/40">Be the first. Generate a notice and check "Join the Resistance" to publish it here.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="ledger-feed">
            {items.map(it => (
              <div key={it.id} className="tactical-card p-5 hover:border-plai-teal/40 transition-colors" data-testid={`ledger-row-${it.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Hammer className="h-4 w-4 text-plai-cta" strokeWidth={1.5} />
                      <span className="font-headings text-lg uppercase tracking-tight">{it.jurisdiction || 'Unknown jurisdiction'}</span>
                    </div>
                    <p className="font-body text-xs text-white/60">
                      To: {it.recipient_name || 'Government official'} | Agency: <span className="text-plai-teal">{it.agency || 'N/A'}</span>
                    </p>
                    {it.statutes_cited?.length > 0 && (
                      <p className="font-body text-[10px] text-white/45 mt-2 uppercase tracking-wide">
                        Cited: {it.statutes_cited.slice(0, 4).join(' | ')}
                        {it.statutes_cited.length > 4 ? ` +${it.statutes_cited.length - 4} more` : ''}
                      </p>
                    )}
                  </div>
                  <div className="font-body text-[10px] tracking-[0.2em] uppercase text-white/40 shrink-0">{it.date}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA strip */}
        <div className="mt-16 glass iridescent-border p-10 text-center" data-testid="ledger-cta-strip">
          <h2 className="font-headings text-3xl sm:text-4xl uppercase tracking-tight mb-3">Add Your Notice</h2>
          <p className="font-body text-sm text-white/70 mb-6 max-w-xl mx-auto">For nine ninety nine a month, you put any government secretary on notice. Then you choose to make it public here.</p>
          <Link to="/auth?mode=signup" className="btn-primary-cta text-base px-8 py-4" data-testid="ledger-bottom-cta">Secure My Land - $9.99/MO</Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
